<template>
  <Teleport to="body">
    <div class="modal-overlay">
      <div class="modal-container" @click="closeAllDropdowns">
        <div class="modal-header">
          <h2 class="modal-title">Set Visibility</h2>
          <button class="close-btn" type="button" aria-label="Close" @click="handleClose">
            <img :src="closeIcon" alt="" class="close-icon" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"><span class="required">*</span> Visibility</label>
            <div class="custom-select" ref="visibilitySelectRef">
              <div
                class="select-trigger"
                :class="{ open: visibilityDropdownOpen }"
                @click.stop="toggleVisibilityDropdown"
              >
                <span class="select-value">{{ visibilityLabel }}</span>
                <img :src="downArrowIcon" alt="" class="select-arrow" />
              </div>
            </div>
          </div>

          <template v-if="form.visibility === 'visible'">
            <div class="form-group">
              <label class="form-label"><span class="required">*</span> Visible to</label>
              <div class="custom-select" ref="visibleToSelectRef">
                <div
                  class="select-trigger"
                  :class="{ open: visibleToDropdownOpen }"
                  @click.stop="toggleVisibleToDropdown"
                >
                  <span class="select-value">{{ visibleToLabel }}</span>
                  <img :src="downArrowIcon" alt="" class="select-arrow" />
                </div>
              </div>
            </div>

            <div v-if="form.visibleTo === 'specified_users'" class="form-group form-group--upload">
              <div class="upload-label-block">
                <span class="required">*</span>
                <div class="upload-label-content">
                  <label class="form-label">Upload a table</label>

                  <template v-if="!uploadedFile">
                    <div class="upload-area">
                      <button class="upload-link" type="button" @click.stop="triggerFileUpload">
                        Click to upload
                      </button>
                      <input
                        ref="fileInputRef"
                        type="file"
                        class="file-input"
                        accept=".xls,.xlsx,.csv"
                        @change.stop="handleFileChange"
                      />
                    </div>
                    <p class="upload-hint">Please upload table to send coupons</p>
                  </template>

                  <div v-else class="file-attachment">
                    <img :src="pathIcon" alt="" class="file-path-icon" />
                    <div class="file-name-row">
                      <span class="file-name">{{ uploadedFile.name }}</span>
                      <button class="remove-file-btn" type="button" aria-label="Remove file" @click="removeFile">
                        <img :src="removeIcon" alt="" class="remove-icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" type="button" @click="handleClose">Cancel</button>
          <button
            class="btn-submit"
            type="button"
            :disabled="submitting"
            @click.prevent="handleSubmit"
          >
            <span v-if="submitting" class="spinner" aria-hidden="true"></span>
            <span v-else>Submit</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="visibilityDropdownOpen"
      class="dropdown-backdrop"
      @click.self="visibilityDropdownOpen = false"
    >
      <div class="select-dropdown" :style="visibilityDropdownStyle">
        <div
          v-for="option in visibilityOptions"
          :key="option.value"
          class="select-option"
          :class="{ selected: form.visibility === option.value }"
          @click="selectVisibility(option.value)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>

    <div
      v-if="visibleToDropdownOpen"
      class="dropdown-backdrop"
      @click.self="visibleToDropdownOpen = false"
    >
      <div class="select-dropdown" :style="visibleToDropdownStyle">
        <div
          v-for="option in visibleToOptions"
          :key="option.value"
          class="select-option"
          :class="{ selected: form.visibleTo === option.value }"
          @click="selectVisibleTo(option.value)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, reactive, ref } from 'vue'
import {
  postCouponSetVisibility,
  readCouponSetVisibilityError,
  type CouponVisibilityValue,
  type CouponVisibleToValue,
} from '@/api/CouponSetVisibility'
import { isCouponMutationSuccess, readCouponApiMessage } from '@/api/couponApi'
import closeIcon from '@/assets/Fill 1@2x.png'
import downArrowIcon from '@/assets/downarrow@2x.png'
import pathIcon from '@/assets/路径@2x.png'
import removeIcon from '@/assets/no.png'

interface Props {
  couponId: string
  recordId: number
  initialVisibility?: CouponVisibilityValue
  initialVisibleTo?: CouponVisibleToValue
}

const props = withDefaults(defineProps<Props>(), {
  initialVisibility: 'visible',
  initialVisibleTo: 'all_users',
})

const emit = defineEmits<{
  close: []
  saved: [visibility: CouponVisibilityValue]
  failed: []
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => undefined,
)

const visibilityOptions = [
  { value: 'visible' as const, label: 'Visible' },
  { value: 'hidden' as const, label: 'Hidden' },
]

const visibleToOptions = [
  { value: 'all_users' as const, label: 'All users' },
  { value: 'specified_users' as const, label: 'Specified users' },
]

const form = reactive({
  visibility: props.initialVisibility,
  visibleTo: props.initialVisibleTo,
})

const submitting = ref(false)
const uploadedFile = ref<File | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const visibilitySelectRef = ref<HTMLElement | null>(null)
const visibleToSelectRef = ref<HTMLElement | null>(null)
const visibilityDropdownOpen = ref(false)
const visibleToDropdownOpen = ref(false)
const visibilityDropdownStyle = ref<Record<string, string>>({})
const visibleToDropdownStyle = ref<Record<string, string>>({})

const visibilityLabel = computed(
  () => visibilityOptions.find((option) => option.value === form.visibility)?.label ?? 'Visible',
)

const visibleToLabel = computed(
  () => visibleToOptions.find((option) => option.value === form.visibleTo)?.label ?? 'All users',
)

function updateDropdownPosition(
  triggerRef: HTMLElement | null,
  styleRef: typeof visibilityDropdownStyle,
): void {
  if (!triggerRef) return
  const rect = triggerRef.getBoundingClientRect()
  styleRef.value = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: '100002',
  }
}

function closeAllDropdowns(): void {
  visibilityDropdownOpen.value = false
  visibleToDropdownOpen.value = false
}

function toggleVisibilityDropdown(): void {
  visibleToDropdownOpen.value = false
  visibilityDropdownOpen.value = !visibilityDropdownOpen.value
  if (visibilityDropdownOpen.value) {
    updateDropdownPosition(visibilitySelectRef.value, visibilityDropdownStyle)
  }
}

function toggleVisibleToDropdown(): void {
  visibilityDropdownOpen.value = false
  visibleToDropdownOpen.value = !visibleToDropdownOpen.value
  if (visibleToDropdownOpen.value) {
    updateDropdownPosition(visibleToSelectRef.value, visibleToDropdownStyle)
  }
}

function selectVisibility(value: CouponVisibilityValue): void {
  form.visibility = value
  visibilityDropdownOpen.value = false

  if (value === 'hidden') {
    clearUploadedFile()
  }
}

function clearUploadedFile(): void {
  uploadedFile.value = null
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

function selectVisibleTo(value: CouponVisibleToValue): void {
  form.visibleTo = value
  visibleToDropdownOpen.value = false

  if (value === 'all_users') {
    clearUploadedFile()
  }
}

function triggerFileUpload(): void {
  fileInputRef.value?.click()
}

function isTableUploadFile(file: File): boolean {
  if (/\.(xls|xlsx|csv)$/i.test(file.name)) return true
  return [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
  ].includes(file.type)
}

function handleFileChange(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null

  if (!file) {
    clearUploadedFile()
    return
  }

  if (!isTableUploadFile(file)) {
    showToast(
      'warning',
      'Please upload an Excel or CSV table (.xls, .xlsx, .csv). Images are not supported here.',
    )
    input.value = ''
    clearUploadedFile()
    return
  }

  uploadedFile.value = file
}

function removeFile(): void {
  clearUploadedFile()
}

function handleClose(): void {
  emit('close')
}

function validateForm(): boolean {
  if (
    form.visibility === 'visible' &&
    form.visibleTo === 'specified_users' &&
    !uploadedFile.value
  ) {
    showToast('warning', 'Please upload a table')
    return false
  }

  return true
}

async function handleSubmit(): Promise<void> {
  if (submitting.value) return
  if (!validateForm()) return
  if (!props.recordId) {
    showToast('error', 'Coupon record not found.')
    return
  }

  submitting.value = true
  try {
    const response = await postCouponSetVisibility({
      id: props.recordId,
      coupon_id: props.couponId,
      visibility: form.visibility,
      visible_to: form.visibility === 'visible' ? form.visibleTo : undefined,
      file: form.visibleTo === 'specified_users' ? uploadedFile.value : null,
    })

    if (isCouponMutationSuccess(response)) {
      emit('saved', form.visibility)
      return
    }

    showToast('error', readCouponApiMessage(response, 'Failed to set visibility'))
    emit('failed')
  } catch (error) {
    console.error('Set visibility error:', error)
    showToast('error', readCouponSetVisibilityError(error, 'Failed to set visibility'))
    emit('failed')
  } finally {
    submitting.value = false
  }
}

function handleDocumentClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (
    target.closest('.custom-select') ||
    target.closest('.select-dropdown') ||
    target.closest('.dropdown-backdrop')
  ) {
    return
  }
  closeAllDropdowns()
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  line-height: 24px;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  height: 32px;
  box-sizing: border-box;
  position: relative;
}

.select-trigger.open {
  border-color: #666666;
}

.select-value {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  opacity: 0.55;
  transition: transform 0.2s;
  flex-shrink: 0;
  position: absolute;
  right: 16px;
}

.select-trigger.open .select-arrow {
  transform: rotate(180deg);
}

.form-group--upload {
  gap: 0;
}

.upload-label-block {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.upload-label-block > .required {
  flex-shrink: 0;
  line-height: 20px;
}

.upload-label-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.upload-label-content .form-label {
  margin: 0;
}

.upload-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-area {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.upload-link {
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #e54848;
  text-decoration: underline;
  text-decoration-color: #e54848;
  text-underline-offset: 2px;
  text-align: left;
  cursor: pointer;
  appearance: none;
}

.file-input {
  display: none;
}

.file-attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.file-path-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.file-name-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: calc(100% - 24px);
}

.file-name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remove-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.upload-hint {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #e54848;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 28px;
}

.btn-cancel {
  width: 80px;
  height: 32px;
  padding: 0 16px;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.btn-cancel:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
}

.btn-submit span {
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;
}

.btn-submit:hover:not(:disabled) {
  background-color: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  justify-content: center;
  padding: 0;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100002;
}

.select-dropdown {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  box-sizing: border-box;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  line-height: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.select-option:hover {
  background-color: #f5f5f5;
}

.select-option.selected {
  background-color: transparent;
  color: #000000;
}
</style>
